import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import { AsyncState } from 'shared/utils/async-state';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDEmptyState from 'ud-ui/components/empty-state';
import UDPagedListSkeleton from 'ud-ui/components/paged-list/components/paged-list-skeleton';
import UDPagedListToolbar, {
  ToolbarFiltersLayout,
  ToolbarSearchMode,
  ToolbarSurface,
} from 'ud-ui/components/paged-list/components/paged-list-toolbar';

const DEFAULT_SKELETON_COUNT = 6;

interface SearchModeArgs {
  status: AsyncState['status'];
  /** Выдачу сужает поиск или фильтр. */
  isNarrowed: boolean;
  /** Ответ уже приходил хотя бы раз. */
  hasSettled: boolean;
}

/**
 * Что стоит на месте поля поиска.
 *
 * - Список сужен — поле на месте всегда, даже если ничего не нашлось: запрос нужно
 *   чем-то стереть, а фильтры рядом не должны съезжать.
 * - Пусто без поиска и фильтров — список пуст сам по себе, поля нет.
 * - Первая загрузка — заглушка размером с поле: пока не пришёл ответ, неизвестно, будет
 *   ли поле. Нарисуй поле сразу — на пустом списке оно появилось бы и тут же исчезло.
 * - Всё остальное (данные, ошибка, перезапрос после первого ответа) — поле.
 */
export const getSearchMode = ({ status, isNarrowed, hasSettled }: SearchModeArgs): ToolbarSearchMode => {
  if (isNarrowed) return 'input';
  if (status === 'empty') return 'hidden';
  if (status === 'loading' && !hasSettled) return 'placeholder';
  return 'input';
};

export interface UDEntityListProps<T> {
  /** Все элементы списка: резать их некому — пагинации здесь нет. */
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Ключ строки. По умолчанию — позиция в списке. */
  getItemKey?: (item: T, index: number) => React.Key;

  /** Какую ветку рисовать; `isRefreshing` притушивает уже показанное. */
  state: AsyncState;

  /** Значение поля поиска. Фильтрует список сам вызывающий. */
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Дополнительные фильтры, рисуются в тулбаре слева, рядом с поиском. */
  extraFilters?: React.ReactNode;
  /**
   * Список сейчас сужен чем-то кроме поиска: выбран чип статуса, задан телефон, вид спорта.
   * Поиск список учитывает сам. Без этого флага пустая выдача по фильтру неотличима от
   * пустого списка: поле поиска пропадёт, а вместо «Ничего не найдено» появится «Создать первую…».
   */
  hasActiveFilters?: boolean;
  /** Кнопки действий над списком, рисуются в тулбаре справа. */
  actions?: React.ReactNode;

  /** Текст, когда список пуст сам по себе. */
  emptyText?: React.ReactNode;
  emptySubtitle?: React.ReactNode;
  /** Кнопка под текстом пустого состояния — обычно «Добавить …». */
  emptyAction?: React.ReactNode;
  /** Текст, когда пуст результат поиска или фильтра. */
  notFoundText?: React.ReactNode;
  errorText?: React.ReactNode;

  className?: string;
  listClassName?: string;
  /**
   * Раскладка самих элементов. По умолчанию — колонка строк; экран с карточками
   * передаёт свою сетку. Значение **заменяет** дефолт, а не дописывается к нему:
   * `display` от двух классов сразу не складывается.
   */
  itemsClassName?: string;
  /** Высота одной заглушки скелетона: у строки и у карточки она разная. */
  skeletonRowHeight?: number;
  /** Сколько заглушек рисовать на первой загрузке. */
  skeletonCount?: number;

  /**
   * Размер страницы. Списку внутри карточки он не нужен — проп существует ради
   * `UDPagedList`, который строится поверх этого компонента и добавляет
   * «На странице» в тот же тулбар.
   */
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  /** Подвал под списком — у `UDPagedList` там пагинатор. */
  footer?: React.ReactNode;
  /**
   * Поверхность тулбара. По умолчанию `plain` — список внутри карточки; `UDPagedList` ставит `card`:
   * у страницы своей карточки вокруг панели нет.
   */
  toolbarSurface?: ToolbarSurface;
  /** Раскладка поиска и `extraFilters` в тулбаре: `inline` по умолчанию, `grid` — для подписанных полей. */
  filtersLayout?: ToolbarFiltersLayout;
}

/**
 * Список сущностей: тулбар с поиском, ветки состояний, строки.
 *
 * Ядро всех списков приложения. Список внутри карточки (состав команды, участники,
 * команды игрока) берёт его напрямую: данные таких вкладок приходят целиком одним
 * запросом, страниц у них нет. Список-страница берёт `UDPagedList`, который
 * дописывает сюда «На странице» и пагинатор — так две разновидности физически не
 * могут разъехаться по виду.
 *
 * Компонент **чисто презентационный**: он не знает ни про URL, ни про RTK Query,
 * ни про роуты — всё приходит пропсами (правило «`ud-ui` не импортирует фичи» из
 * `.claude/rules/architecture.md`).
 *
 * Ветку рендера выбирает только `state.status`; «идёт запрос» само по себе список
 * не стирает — скелетон показывается лишь на первой загрузке
 * (`.claude/docs/async-loading-states.md`).
 *
 * Пока список пуст сам по себе — ни поиск, ни фильтр его не сужают, — поле поиска не рисуется:
 * искать нечего. Правило поля одно: оно может появиться, когда пришли данные, но не исчезает
 * под рукой у пользователя. См. `getSearchMode`.
 */
function UDEntityList<T>({
  items,
  renderItem,
  getItemKey,
  state,
  search,
  onSearchChange,
  searchPlaceholder,
  extraFilters,
  hasActiveFilters = false,
  actions,
  emptyText: emptyTextProp,
  emptySubtitle,
  emptyAction,
  notFoundText: notFoundTextProp,
  errorText: errorTextProp,
  className,
  listClassName,
  itemsClassName = 'flex flex-col gap-2.5',
  skeletonRowHeight,
  skeletonCount,
  perPage,
  onPerPageChange,
  footer,
  toolbarSurface,
  filtersLayout,
}: UDEntityListProps<T>) {
  const { t } = useLingui();
  const emptyText = emptyTextProp ?? t`Список пуст`;
  const notFoundText = notFoundTextProp ?? t`Ничего не найдено`;
  const errorText = errorTextProp ?? t`Не удалось загрузить список`;
  const isNarrowed = search.trim().length > 0 || hasActiveFilters;

  // Пришёл ли хоть один ответ. Ссылка, а не состояние: меняется ровно в том рендере, где ответ
  // появился, без лишнего кадра (как `displayedOffsetRef` у `usePagedList`).
  const hasSettledRef = useRef(false);
  if (state.status !== 'loading') hasSettledRef.current = true;

  const searchMode = getSearchMode({ status: state.status, isNarrowed, hasSettled: hasSettledRef.current });

  const empty = useMemo(
    () =>
      isNarrowed ? (
        // Кнопка создания не предлагается: пусто не потому, что ничего нет,
        // а потому, что ничего не совпало с поиском или фильтром.
        <UDEmptyState title={notFoundText} />
      ) : (
        <UDEmptyState title={emptyText} subtitle={emptySubtitle} action={emptyAction} />
      ),
    [isNarrowed, notFoundText, emptyText, emptySubtitle, emptyAction],
  );

  return (
    <div className={classNames('flex h-full flex-col', className)}>
      <UDPagedListToolbar
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        // Ветка `loading` при непустом поиске и есть «запрос в пути»; когда список
        // уже на экране, о том же говорит `isRefreshing`.
        isSearchLoading={state.isRefreshing || state.status === 'loading'}
        searchMode={searchMode}
        surface={toolbarSurface}
        filtersLayout={filtersLayout}
        perPage={perPage}
        onPerPageChange={onPerPageChange}
        extraFilters={extraFilters}
        actions={actions}
      />

      <UDAsyncContent
        state={state}
        data-testid="paged-list-content"
        className={classNames('flex-1', listClassName)}
        loading={
          <UDPagedListSkeleton
            count={skeletonCount ?? perPage ?? DEFAULT_SKELETON_COUNT}
            className={itemsClassName}
            rowHeight={skeletonRowHeight}
          />
        }
        empty={empty}
        error={<UDEmptyState title={errorText} />}
      >
        <div className={itemsClassName} role="list">
          {items.map((item, index) => (
            <React.Fragment key={getItemKey ? getItemKey(item, index) : index}>{renderItem(item, index)}</React.Fragment>
          ))}
        </div>
      </UDAsyncContent>

      {footer}
    </div>
  );
}

export default UDEntityList;
